import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";
import BrandHeader from "../components/BrandHeader";
import { clientSignup } from "../lib/clientAuth";
import { useClientAuth } from "../lib/clientAuthContext";

// Account-creation screen shown BEFORE the Roadmap questionnaire.
// Collects: full name, preferred name, email, password.

export default function AuditSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useClientAuth();
  const dest = location.state?.from || "/audit";

  const [form, setForm] = useState({ full_name: "", preferred_name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const canSubmit =
    form.full_name.trim().length > 0 &&
    form.preferred_name.trim().length > 0 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.password.length >= 8;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      await clientSignup({
        full_name: form.full_name.trim(),
        preferred_name: form.preferred_name.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      refresh();
      navigate(dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't create your account. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-base flex flex-col">
      <Toaster richColors position="top-center" />
      <BrandHeader small />

      <main className="flex-1 px-6 md:px-12 lg:px-16 py-10 md:py-14 max-w-2xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="label-eyebrow flex items-center gap-3" data-testid="signup-eyebrow">
            <span className="inline-block h-px w-10 bg-brand-primary" />
            Create your HER Aligned account
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink leading-tight mt-4" data-testid="signup-headline">
            Let's set you up first, beautiful.
          </h1>
          <p className="mt-5 text-brand-ink-muted text-base md:text-lg leading-relaxed">
            This sets up your <strong className="text-brand-ink">HER Aligned account</strong> so your Personalised Roadmap,
            Weekly Check-Ins and personalised feedback are all securely kept together in one place.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-6" noValidate>
            <div>
              <label className="label-eyebrow block mb-2" htmlFor="full-name">Full name</label>
              <input
                id="full-name"
                type="text"
                autoComplete="name"
                className="input-warm text-lg"
                placeholder="Jane Smith"
                value={form.full_name}
                onChange={(e) => update({ full_name: e.target.value })}
                data-testid="signup-full-name-input"
                autoFocus
              />
            </div>

            <div>
              <label className="label-eyebrow block mb-2" htmlFor="preferred-name">
                What would you prefer me to call you?
              </label>
              <input
                id="preferred-name"
                type="text"
                autoComplete="nickname"
                className="input-warm text-lg"
                placeholder="Jane"
                value={form.preferred_name}
                onChange={(e) => update({ preferred_name: e.target.value })}
                data-testid="signup-preferred-name-input"
              />
              <p className="mt-2 text-sm text-brand-ink-muted">
                This is the name Justine and your Roadmap will use throughout the app.
              </p>
            </div>

            <div>
              <label className="label-eyebrow block mb-2" htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className="input-warm text-lg"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => update({ email: e.target.value })}
                data-testid="signup-email-input"
              />
            </div>

            <div>
              <label className="label-eyebrow block mb-2" htmlFor="password">Create password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  className="input-warm text-lg pr-24"
                  placeholder="At least 8 characters"
                  value={form.password}
                  onChange={(e) => update({ password: e.target.value })}
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-brand-ink-muted hover:text-brand-primary transition-colors"
                  data-testid="signup-show-password-btn"
                >
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>
              <p className="mt-2 text-sm text-brand-ink-muted">
                Minimum 8 characters, with an uppercase letter, lowercase letter and a number.
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-brand-ink-muted pt-2">
              <ShieldCheck size={16} strokeWidth={1.5} className="text-brand-primary" />
              Your password is stored securely (hashed). We never see it.
            </div>

            <button
              type="submit"
              disabled={!canSubmit || busy}
              className="btn-primary inline-flex items-center gap-2.5 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="signup-submit-btn"
            >
              {busy ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Creating your account…
                </>
              ) : (
                <>
                  Create My Account &amp; Start My Roadmap
                  <ArrowRight size={18} strokeWidth={2} />
                </>
              )}
            </button>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
